import React from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Tooltip,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { useTranslation } from 'react-i18next';

const OMOP_MAPPING_ACTIONS_COPY = {
  de: {
    validateTooltip: 'Mapping als korrekt validieren',
    validate: 'Validieren',
    rejectTooltip: 'Mapping ablehnen',
    reject: 'Ablehnen',
    generateTooltip: 'AI-gestütztes Mapping generieren lassen',
    generating: 'Generiere...',
    aiAssistant: 'KI-Assistent',
    criticTooltip: 'Geben Sie der KI konkrete Hinweise für einen besseren Mapping-Vorschlag',
    critic: 'KI-Vorschlag verbessern',
  },
  en: {
    validateTooltip: 'Validate the mapping as correct',
    validate: 'Validate',
    rejectTooltip: 'Reject the mapping',
    reject: 'Reject',
    generateTooltip: 'Generate an AI-supported mapping suggestion',
    generating: 'Generating...',
    aiAssistant: 'AI assistant',
    criticTooltip: 'Give the AI concrete hints for a better mapping suggestion',
    critic: 'Improve AI suggestion',
  },
  pt: {
    validateTooltip: 'Validar o mapeamento como correto',
    validate: 'Validar',
    rejectTooltip: 'Rejeitar o mapeamento',
    reject: 'Rejeitar',
    generateTooltip: 'Gerar uma sugestão de mapeamento com IA',
    generating: 'A gerar...',
    aiAssistant: 'Assistente IA',
    criticTooltip: 'Dê indicações concretas à IA para uma sugestão de mapeamento melhor',
    critic: 'Melhorar sugestão da IA',
  },
};

/**
 * OmopMappingActions Component
 * Action buttons for OMOP mapping workflows
 * Handles validate, reject, edit, generate, and feedback actions
 *
 * @param {object} props
 * @param {string} props.omopStatus - Current mapping status
 * @param {function} props.onValidate - Callback for validate action
 * @param {function} props.onReject - Callback for reject action
 * @param {function} props.onEdit - Callback for edit/manual mapping action
 * @param {function} props.onGenerateMapping - Callback for AI mapping generation
 * @param {function} props.onOpenCritic - Callback to open feedback dialog
 * @param {boolean} props.isProcessing - Whether an action is in progress
 * @param {boolean} props.hasAiMapping - Whether AI mapping exists
 */
const OmopMappingActions = ({
  omopStatus,
  onValidate,
  onReject,
  onEdit,
  onGenerateMapping,
  onOpenCritic,
  isProcessing = false,
  hasAiMapping = false
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = OMOP_MAPPING_ACTIONS_COPY[language];

  // Determine which actions are available based on status
  const showValidateReject = ['mapped', 'pending'].includes(omopStatus) && hasAiMapping;
  const showGenerate = ['unmapped', 'pending'].includes(omopStatus);
  const showCritic = hasAiMapping && omopStatus !== 'validated';

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {/* Validate & Reject Group */}
      {showValidateReject && (
        <ButtonGroup variant="contained" disabled={isProcessing}>
          <Tooltip title={copy.validateTooltip}>
            <Button
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={onValidate}
            >
              {copy.validate}
            </Button>
          </Tooltip>
          <Tooltip title={copy.rejectTooltip}>
            <Button
              color="error"
              startIcon={<CancelIcon />}
              onClick={onReject}
            >
              {copy.reject}
            </Button>
          </Tooltip>
        </ButtonGroup>
      )}

      {/* Generate AI Mapping */}
      {showGenerate && (
        <Tooltip title={copy.generateTooltip}>
          <Button
            variant="contained"
            startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <AutoAwesomeIcon />}
            onClick={onGenerateMapping}
            disabled={isProcessing}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4193 100%)',
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                opacity: 0.6,
              }
            }}
          >
            {isProcessing ? copy.generating : copy.aiAssistant}
          </Button>
        </Tooltip>
      )}

      {/* AI Improvement Feedback */}
      {showCritic && (
        <Tooltip title={copy.criticTooltip}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PsychologyIcon />}
            onClick={onOpenCritic}
            disabled={isProcessing}
          >
            {copy.critic}
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

export default OmopMappingActions;
